import React from "react";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { Label } from "../Label";
import { Button } from "../Button";
import { MultiSelectSearch } from "./MultiSelectSearch";
import { usePostalCodes } from "../../hooks/usePostalCodes";
import {
  TargetingFormState,
  TARGETING_RADIUS_MAX_KM,
  TARGETING_RADIUS_MIN_KM,
} from "../../helpers/popupCampaignTargeting";

// Leaflet ships its marker images as relative URLs that a bundler rewrites away. Import the
// images already bundled inside the installed package rather than pointing at unpkg.com — a
// third-party runtime dependency and a CSP hazard in an admin tool, for images already on disk.
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/** Denmark, roughly centred, at a zoom that shows the whole country. */
const DEFAULT_CENTER: [number, number] = [56.0, 10.5];
const DEFAULT_ZOOM = 6;
const DEFAULT_RADIUS_KM = "15";

interface Props {
  form: TargetingFormState;
  onChange: (patch: Partial<TargetingFormState>) => void;
  /** Postal codes the current selection resolves to, from the server. Null while unknown. */
  resolvedCount: number | null;
  resolving: boolean;
  /** True when the last resolve request failed — distinct from "not yet run" (null count with
   * no error) so a failure is visible instead of silently rendering nothing. */
  resolveFailed?: boolean;
  error?: string;
}

const ClickToPlacePin: React.FC<{ onPlace: (lat: number, lon: number) => void }> = ({ onPlace }) => {
  useMapEvents({
    click: (event) => onPlace(event.latlng.lat, event.latlng.lng),
  });
  return null;
};

export const LocationPicker: React.FC<Props> = ({
  form, onChange, resolvedCount, resolving, resolveFailed, error,
}) => {
  const { options, loading, error: postalCodesError } = usePostalCodes();

  const hasPin = form.radiusLat !== null && form.radiusLon !== null;
  const radiusKm = Number(form.radiusKm) || 0;

  const placePin = (lat: number, lon: number) =>
    onChange({
      radiusLat: lat,
      radiusLon: lon,
      radiusKm: form.radiusKm === "" ? DEFAULT_RADIUS_KM : form.radiusKm,
    });

  return (
    <div className="space-y-4">
      {postalCodesError && (
        <p className="text-xs text-red-600">
          {postalCodesError} — postal code, city and region search will show no results until this loads.
        </p>
      )}

      <div>
        <div className="flex items-center justify-between">
          <Label>Area on the map</Label>
          {hasPin && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onChange({ radiusLat: null, radiusLon: null, radiusKm: "" })}
            >
              Clear pin
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1 mb-2">
          Click the map to place a pin, then set a radius. Leave it empty to target by postal code,
          city or region only.
        </p>

        <div className="h-72 rounded-lg overflow-hidden border border-gray-200">
          <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickToPlacePin onPlace={placePin} />
            {hasPin && (
              <>
                <Marker
                  position={[form.radiusLat!, form.radiusLon!]}
                  draggable
                  eventHandlers={{
                    dragend: (event) => {
                      const { lat, lng } = event.target.getLatLng();
                      placePin(lat, lng);
                    },
                  }}
                />
                {radiusKm > 0 && (
                  <Circle center={[form.radiusLat!, form.radiusLon!]} radius={radiusKm * 1000} />
                )}
              </>
            )}
          </MapContainer>
        </div>

        {hasPin && (
          <div className="flex items-center gap-3 mt-2">
            <input
              type="range"
              min={TARGETING_RADIUS_MIN_KM}
              max={TARGETING_RADIUS_MAX_KM}
              value={radiusKm || TARGETING_RADIUS_MIN_KM}
              onChange={(e) => onChange({ radiusKm: e.target.value })}
              className="flex-1"
              aria-label="Radius in kilometres"
            />
            <span className="text-sm tabular-nums w-16 text-right">{radiusKm} km</span>
          </div>
        )}
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>

      <MultiSelectSearch
        label="Postal codes"
        options={options.codes}
        selected={form.locationPostalCodes}
        onChange={(next) => onChange({ locationPostalCodes: next })}
        placeholder={loading ? "Loading…" : "Search postal codes"}
      />

      <MultiSelectSearch
        label="Cities"
        options={options.cities}
        selected={form.locationCities}
        onChange={(next) => onChange({ locationCities: next })}
        placeholder={loading ? "Loading…" : "Search cities"}
      />

      <MultiSelectSearch
        label="Regions"
        options={options.regions}
        selected={form.locationRegions}
        onChange={(next) => onChange({ locationRegions: next })}
        placeholder={loading ? "Loading…" : "Search regions"}
      />

      <p className="text-xs text-gray-500">
        The map, postal codes, cities and regions combine — a user in any of them matches.{" "}
        {resolving && "Counting postal codes…"}
        {!resolving && resolveFailed && (
          <span className="text-red-600 font-medium">
            Couldn't check this selection — try again in a moment.
          </span>
        )}
        {!resolving && !resolveFailed && resolvedCount !== null && (
          <span className={resolvedCount === 0 ? "text-red-600 font-medium" : ""}>
            {resolvedCount === 0
              ? "This selection covers no postal codes, so nobody could see the campaign."
              : `Covers ${resolvedCount} postal ${resolvedCount === 1 ? "code" : "codes"}.`}
          </span>
        )}
      </p>
    </div>
  );
};
